/**
 * Config persistence.
 *
 * Design decision: the overlay is stored as a FLAT map of `dot.path -> value`, never as a nested
 * copy of the whole config. Consequences that matter:
 *
 *  - Renaming or removing a tunable drops exactly that entry, with a visible warning, instead of
 *    resurrecting a stale nested shape.
 *  - New defaults are picked up automatically; the overlay only ever holds deliberate deviations.
 *  - "Blind deep merge" — which would happily merge `{x: 0.1}` over a three-axis vector and leave
 *    the other two axes from an older shape — cannot happen.
 *
 * Failure policy (plan §5.1): a corrupt or hostile overlay must never brick the demo. Parsing is
 * guarded, every value is validated through `schema.ts`, and `?reset=1` is honoured BEFORE any
 * load so a wedged device can always be recovered from the URL alone.
 */

import { DEFAULTS, type Config } from './defaults';
import { FIELD_BY_PATH, type Field } from './schema';

/** Bump this whenever the config shape changes. Old overlays are then ignored wholesale. */
export const STORAGE_KEY = 'npay-ar-demo.config.v1';

/** URL parameter that clears the overlay before it is read. */
export const RESET_PARAM = 'reset';

export type Overlay = Record<string, unknown>;

export interface LoadResult {
  readonly config: Config;
  /** Human-readable problems worth surfacing on screen and in the debug HUD. */
  readonly warnings: readonly string[];
  /** The stored overlay could not be parsed at all. */
  readonly corrupt: boolean;
  /** `?reset=1` was present, so the overlay was cleared. */
  readonly wasReset: boolean;
}

export function defaultConfig(): Config {
  return structuredClone(DEFAULTS) as unknown as Config;
}

// ---------------------------------------------------------------------------
// Path access
// ---------------------------------------------------------------------------

export function getByPath(root: unknown, path: string): unknown {
  let cur: unknown = root;
  for (const key of path.split('.')) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

function setByPath(root: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.');
  const last = keys.pop();
  if (last === undefined) return;
  let cur: Record<string, unknown> = root;
  for (const key of keys) {
    const next = cur[key];
    if (next === null || typeof next !== 'object') return; // path does not exist in the shape
    cur = next as Record<string, unknown>;
  }
  cur[last] = value;
}

// ---------------------------------------------------------------------------
// Validation — driven by schema.ts, never by `typeof` alone
// ---------------------------------------------------------------------------

const COLOR_MAX = 0xffffff;
const RANGE2_LEN = 2;

/** @returns the accepted value, or a rejection reason. */
export function validateField(field: Field, value: unknown): { ok: true; value: unknown } | { ok: false; reason: string } {
  if (value === null) {
    return field.nullable === true ? { ok: true, value: null } : { ok: false, reason: 'null is not allowed' };
  }

  const inRange = (n: number): string | null => {
    if (!Number.isFinite(n)) return 'not a finite number';
    if (field.min !== undefined && n < field.min) return `below minimum ${field.min}`;
    if (field.max !== undefined && n > field.max) return `above maximum ${field.max}`;
    return null;
  };

  switch (field.kind) {
    case 'number':
    case 'int': {
      if (typeof value !== 'number') return { ok: false, reason: `expected number, got ${typeof value}` };
      const bad = inRange(value);
      if (bad) return { ok: false, reason: bad };
      if (field.kind === 'int' && !Number.isInteger(value)) return { ok: false, reason: 'expected an integer' };
      return { ok: true, value };
    }
    case 'color': {
      if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > COLOR_MAX) {
        return { ok: false, reason: 'expected an integer colour 0x000000–0xffffff' };
      }
      return { ok: true, value };
    }
    case 'boolean': {
      if (typeof value !== 'boolean') return { ok: false, reason: `expected boolean, got ${typeof value}` };
      return { ok: true, value };
    }
    case 'enum': {
      if (typeof value !== 'string' || !field.options?.includes(value)) {
        return { ok: false, reason: `expected one of ${field.options?.join(' | ')}` };
      }
      return { ok: true, value };
    }
    case 'text':
    case 'multiline': {
      if (typeof value !== 'string') return { ok: false, reason: `expected string, got ${typeof value}` };
      return { ok: true, value };
    }
    case 'range2': {
      if (!Array.isArray(value) || value.length !== RANGE2_LEN) {
        return { ok: false, reason: `expected a 2-element array, got ${Array.isArray(value) ? `length ${value.length}` : typeof value}` };
      }
      const [lo, hi] = value as unknown[];
      if (typeof lo !== 'number' || typeof hi !== 'number') return { ok: false, reason: 'both elements must be numbers' };
      const bad = inRange(lo) ?? inRange(hi);
      if (bad) return { ok: false, reason: bad };
      if (lo > hi) return { ok: false, reason: `min ${lo} is greater than max ${hi}` };
      return { ok: true, value: [lo, hi] };
    }
    default:
      return { ok: false, reason: 'unknown field kind' };
  }
}

/** Apply an overlay onto a fresh default config, dropping anything that does not validate. */
export function applyOverlay(overlay: Overlay): { config: Config; warnings: string[] } {
  const config = defaultConfig();
  const warnings: string[] = [];

  for (const [path, value] of Object.entries(overlay)) {
    const field = FIELD_BY_PATH.get(path);
    if (!field) {
      warnings.push(`dropped unknown setting "${path}" (renamed or removed)`);
      continue;
    }
    if (field.readOnly === true) {
      warnings.push(`ignored read-only setting "${path}"`);
      continue;
    }
    const result = validateField(field, value);
    if (!result.ok) {
      warnings.push(`ignored "${path}": ${result.reason} — using the default`);
      continue;
    }
    setByPath(config as unknown as Record<string, unknown>, path, result.value);
  }

  return { config, warnings };
}

// ---------------------------------------------------------------------------
// Load / save
// ---------------------------------------------------------------------------

export function loadConfig(search: string = globalThis.location?.search ?? '', storage: Storage | null = safeStorage()): LoadResult {
  // Honoured BEFORE reading anything: this is the only recovery path for a wedged device.
  const wasReset = new URLSearchParams(search).get(RESET_PARAM) === '1';
  if (wasReset) {
    try {
      storage?.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable — nothing to clear */
    }
    return { config: defaultConfig(), warnings: [], corrupt: false, wasReset: true };
  }

  let raw: string | null = null;
  try {
    raw = storage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    return { config: defaultConfig(), warnings: ['storage is unavailable — settings will not persist'], corrupt: false, wasReset: false };
  }
  if (raw === null) return { config: defaultConfig(), warnings: [], corrupt: false, wasReset: false };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // A truncated or hand-edited overlay must not throw during boot.
    return { config: defaultConfig(), warnings: ['saved settings were unreadable'], corrupt: true, wasReset: false };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { config: defaultConfig(), warnings: ['saved settings had an unexpected shape'], corrupt: true, wasReset: false };
  }

  const { config, warnings } = applyOverlay(parsed as Overlay);
  return { config, warnings, corrupt: false, wasReset: false };
}

/** Persist only the paths that deviate from the defaults. */
export function saveConfig(config: Config, storage: Storage | null = safeStorage()): Overlay {
  const overlay: Overlay = {};
  const defaults = DEFAULTS as unknown as Record<string, unknown>;

  for (const field of FIELD_BY_PATH.values()) {
    if (field.readOnly === true) continue;
    const current = getByPath(config, field.path);
    const original = getByPath(defaults, field.path);
    if (!deepEqual(current, original)) overlay[field.path] = current;
  }

  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(overlay));
  } catch {
    /* quota or private mode — the in-memory config still applies for this session */
  }
  return overlay;
}

export function resetConfig(storage: Storage | null = safeStorage()): void {
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
}

export function exportJson(config: Config): string {
  const overlay: Overlay = {};
  const defaults = DEFAULTS as unknown as Record<string, unknown>;
  for (const field of FIELD_BY_PATH.values()) {
    if (field.readOnly === true) continue;
    const current = getByPath(config, field.path);
    if (!deepEqual(current, getByPath(defaults, field.path))) overlay[field.path] = current;
  }
  return JSON.stringify(overlay, null, 2);
}

export function importJson(text: string): { ok: boolean; warnings: string[]; config: Config } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    return { ok: false, warnings: [`not valid JSON: ${(err as Error).message}`], config: defaultConfig() };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, warnings: ['expected a JSON object of "path": value entries'], config: defaultConfig() };
  }
  const { config, warnings } = applyOverlay(parsed as Overlay);
  return { ok: true, warnings, config };
}

// ---------------------------------------------------------------------------

function safeStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null; // private mode / blocked cookies
  }
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => deepEqual(v, b[i]));
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const ka = Object.keys(a as object);
    const kb = Object.keys(b as object);
    return (
      ka.length === kb.length &&
      ka.every((k) => deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]))
    );
  }
  return false;
}
